import React from 'react';
import { Tag, Check, Plus, ChevronRight } from 'lucide-react';
import { calculateFullPrice, BOOKING_TYPES, BOOKING_OPTIONS, BOOKING_POLICY } from '../../config/pricingRules';
import { formatDate } from '../../utils/dateUtils';

/**
 * PriceBreakdown — Récapitulatif tarifaire détaillé
 * Affiche : prix/jour, réductions, options, total
 *
 * Props:
 *   pricePerDay  : number  — tarif journalier de base
 *   nbDays       : number  — nombre de jours
 *   bookingType  : string  — '1j' | 'demi_matin' | 'demi_aprem' | ...
 *   startDate    : string  — date de début (YYYY-MM-DD)
 *   endDate      : string  — date de fin (YYYY-MM-DD)
 *   options      : array   — ['livraison_veille', 'recuperation_surlen']
 *   qty          : number  — quantité
 *   dark         : bool    — thème sombre
 *   showAcompte  : bool    — afficher acompte/solde
 */
export default function PriceBreakdown({
  pricePerDay = 0,
  nbDays = 1,
  bookingType = '1j',
  startDate = null,
  endDate = null,
  options = [],
  qty = 1,
  dark = false,
  showAcompte = true,
}) {
  const breakdown = calculateFullPrice({ pricePerDay, nbDays, bookingType, startDate, options, qty });
  const bType = BOOKING_TYPES.find(b => b.key === bookingType) || BOOKING_TYPES[2];

  const ink    = dark ? '#fff'                    : 'var(--ink)';
  const muted  = dark ? 'rgba(255,255,255,0.4)'  : 'var(--text-muted)';
  const bg     = dark ? '#161616'                : '#FFFFFF';
  const border = dark ? 'rgba(255,255,255,0.08)' : 'var(--border)';
  const teal   = '#5DBCBF';
  const gold   = '#D4AF37';
  const green  = '#1F9D7A';

  if (!pricePerDay || pricePerDay <= 0) return null;

  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${border}`, background: bg }}>
      {/* Titre */}
      <div className="px-4 py-3 flex items-center justify-between"
        style={{ borderBottom: `1px solid ${border}`, background: dark ? 'rgba(255,255,255,0.02)' : 'var(--bg)' }}>
        <p className="text-xs font-bold uppercase tracking-wide" style={{ color: muted }}>Détail tarifaire</p>
        {startDate && (
          <p className="text-xs font-semibold" style={{ color: teal }}>
            {formatDate(startDate)}{endDate && endDate !== startDate ? ` → ${formatDate(endDate)}` : ''}
          </p>
        )}
      </div>

      <div className="p-4 space-y-3">

        {/* ─ Ligne 1 : Prix de base ─ */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-base">{bType.icon}</span>
            <div>
              <p className="text-sm font-semibold" style={{ color: ink }}>{bType.label}</p>
              {nbDays > 1 && bookingType !== 'demi_matin' && bookingType !== 'demi_aprem' && (
                <p className="text-xs" style={{ color: muted }}>{pricePerDay}€ / jour × {nbDays} jour{nbDays > 1 ? 's' : ''}</p>
              )}
              {(bookingType === 'demi_matin' || bookingType === 'demi_aprem') && (
                <p className="text-xs" style={{ color: muted }}>50% de {pricePerDay}€</p>
              )}
            </div>
          </div>
          <span className="font-bold" style={{ color: ink }}>{breakdown.basePrice.toFixed(2)}€</span>
        </div>

        {/* ─ Réductions ─ */}
        {breakdown.reductions.map((r, i) => (
          <div key={i} className="flex justify-between items-center px-3 py-2 rounded-xl"
            style={{ background: `${r.color}10`, border: `1px solid ${r.color}25` }}>
            <div className="flex items-center gap-1.5">
              <Tag size={11} style={{ color: r.color }} />
              <p className="text-xs font-semibold" style={{ color: r.color }}>{r.label}</p>
            </div>
            <span className="text-xs font-bold" style={{ color: r.color }}>-{r.saving.toFixed(2)}€</span>
          </div>
        ))}

        {/* ─ Quantité ─ */}
        {qty > 1 && (
          <div className="flex justify-between items-center text-xs" style={{ color: muted }}>
            <span>Quantité : {qty}</span>
            <span>{breakdown.subtotal.toFixed(2)}€</span>
          </div>
        )}

        {/* ─ Options ─ */}
        {breakdown.optionsDetails.map(opt => (
          <div key={opt.key} className="flex justify-between items-center px-3 py-2 rounded-xl"
            style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)' }}>
            <div className="flex items-center gap-1.5">
              <Plus size={11} style={{ color: gold }} />
              <p className="text-xs font-semibold" style={{ color: gold }}>{opt.label}</p>
            </div>
            <span className="text-xs font-bold" style={{ color: gold }}>+{opt.prix}€</span>
          </div>
        ))}

        {/* ─ Séparateur ─ */}
        <div style={{ borderTop: `1px dashed ${border}` }} />

        {/* ─ Total ─ */}
        <div className="flex justify-between items-center">
          <p className="text-sm font-bold" style={{ color: ink }}>Total TTC</p>
          <p className="text-xl font-bold" style={{ color: gold }}>{breakdown.total.toFixed(2)}€</p>
        </div>

        {/* ─ Acompte / Solde ─ */}
        {showAcompte && (
          <div className="grid grid-cols-2 gap-2 pt-1">
            <div className="p-2.5 rounded-xl text-center"
              style={{ background: 'rgba(93,188,191,0.08)', border: '1px solid rgba(93,188,191,0.2)' }}>
              <p className="text-xs" style={{ color: muted }}>Acompte 30%</p>
              <p className="text-sm font-bold" style={{ color: teal }}>{breakdown.acompte.toFixed(2)}€</p>
            </div>
            <div className="p-2.5 rounded-xl text-center"
              style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)' }}>
              <p className="text-xs" style={{ color: muted }}>Solde 70%</p>
              <p className="text-sm font-bold" style={{ color: gold }}>{breakdown.solde.toFixed(2)}€</p>
            </div>
          </div>
        )}

        {/* ─ Info horaires livraison ─ */}
        {bType.delivery && (
          <p className="text-xs flex items-center gap-1.5" style={{ color: muted }}>
            <Check size={11} style={{ color: green }} />
            {bType.delivery}
          </p>
        )}
      </div>
    </div>
  );
}
